import { ReactNode, useState, useEffect, useCallback } from 'react';
import { ServicesContextObject, ServicesContextType } from './ServicesContextObject';
import { 
  fetchAllServices, 
  fetchAllServiceTypes,
  addService as apiAddService,
  updateService as apiUpdateService,
  deleteService as apiDeleteService 
} from '../api';
import type { Service, ServiceWithChecklist, ServiceType } from '../types';

interface ServicesContextProps {
  children: ReactNode;
}

function ServicesContext({ children }: ServicesContextProps) {
  const [services, setServices] = useState<Service[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [servicesData, typesData] = await Promise.all([
        fetchAllServices(),
        fetchAllServiceTypes()
      ]);
      setServices(servicesData);
      setServiceTypes(typesData);
    } catch (err) {
      console.error('Failed to load services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load services on mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [servicesData, typesData] = await Promise.all([
          fetchAllServices(),
          fetchAllServiceTypes()
        ]);
        if (isMounted) {
          setServices(servicesData);
          setServiceTypes(typesData);
        }
      } catch (err) {
        console.error('Failed to load services:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load services');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Note: Real-time subscription removed - services are static data that rarely changes.
  // Data is refetched on component mount and when needed.

  const addService = async (serviceData: Omit<ServiceWithChecklist, 'id'>): Promise<Service> => {
    try {
      const newService = await apiAddService(serviceData);
      setServices(prev => [newService, ...prev]);
      return newService;
    } catch (err) {
      console.error('Error adding service:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to add service';
      setError(errorMessage);
      throw err;
    }
  };

  const updateService = async (
    serviceId: string, 
    serviceData: Partial<Omit<ServiceWithChecklist, 'id'>>
  ): Promise<Service> => {
    try {
      const updatedService = await apiUpdateService(serviceId, serviceData);
      setServices(prev => prev.map(service => 
        service.id === serviceId ? updatedService : service
      ));
      return updatedService;
    } catch (err) {
      console.error('Error updating service:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update service';
      setError(errorMessage);
      throw err;
    }
  };

  const deleteService = async (serviceId: string): Promise<void> => {
    try {
      await apiDeleteService(serviceId);
      setServices(prev => prev.filter(service => service.id !== serviceId));
    } catch (err) {
      console.error('Error deleting service:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete service';
      setError(errorMessage);
      throw err;
    }
  };

  const refreshServices = async (): Promise<void> => {
    await loadServices();
  };

  const getServiceById = (serviceId: string): Service | undefined => {
    return services.find(service => service.id === serviceId);
  };

  const getServiceTypes = (serviceTypeId: string): ServiceType | undefined => {
    return serviceTypes.find(type => String(type.id) === serviceTypeId);
  };

  const value: ServicesContextType = {
    service_types: serviceTypes,
    services,
    loading,
    error,
    addService,
    updateService,
    deleteService,
    refreshServices,
    getServiceById,
    getServiceTypes
  };

  return (
    <ServicesContextObject.Provider value={value}>
      {children}
    </ServicesContextObject.Provider>
  );
}

interface ServicesDataProviderProps {
  children: ReactNode;
}

/**
 * Data provider wrapper for services that matches the pattern used by other features.
 * Since services are self-contained, this simply wraps the ServicesContext.
 */
export function ServicesDataProvider({ children }: ServicesDataProviderProps) {
  return (
    <ServicesContext>
      {children}
    </ServicesContext>
  );
}